import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// DTC-L: route OptiBay onboarding lifecycle writes through clock.
import { clock } from '@/utils/clock';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Plus, Trash2, Wrench, Clock } from 'lucide-react';
import { useSessionRepairs, useAddRepair, useUpdateRepair, useRemoveRepair } from '@/hooks/useOptiBayRepair';
import { useSessionInterpretations } from '@/hooks/useOptiBayInterpretation';
import type { OptiBaySession, OptiBayRepairStatus } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayRepairStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

const REPAIR_STATUS_CONFIG: Record<OptiBayRepairStatus, { label: string; color: string }> = {
  planned: { label: 'Planned', color: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
};

export function OptiBayRepairStep({ session, onNext, onBack }: OptiBayRepairStepProps) {
  const { data: repairs = [] } = useSessionRepairs(session.id);
  const { data: interpretations = [] } = useSessionInterpretations(session.id);
  const addRepair = useAddRepair();
  const updateRepair = useUpdateRepair();
  const removeRepair = useRemoveRepair();

  const [showForm, setShowForm] = useState(false);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [partsNeeded, setPartsNeeded] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  const [selectedInterpretationId, setSelectedInterpretationId] = useState('');

  const handleAdd = async () => {
    if (!description.trim()) return;
    await addRepair.mutateAsync({
      sessionId: session.id,
      interpretationId: selectedInterpretationId || undefined,
      repairDescription: description.trim(),
      repairCategory: category.trim() || undefined,
      partsNeeded: partsNeeded.trim() || undefined,
      estimatedMinutes: estimatedMinutes ? parseInt(estimatedMinutes) : undefined,
    });
    setDescription('');
    setCategory('');
    setPartsNeeded('');
    setEstimatedMinutes('');
    setSelectedInterpretationId('');
    setShowForm(false);
  };

  const handleStatusChange = async (repairId: string, status: OptiBayRepairStatus) => {
    await updateRepair.mutateAsync({
      id: repairId,
      updates: {
        status,
        completed_at: status === 'completed' ? clock.now().toISOString() : null,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Repair Plan</h4>
        <Badge variant="secondary" className="text-xs">
          {repairs.length} repair{repairs.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {repairs.length === 0 && !showForm ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          No repairs planned. Based on your interpretation, define the repair actions needed.
        </p>
      ) : (
        <div className="space-y-2">
          {repairs.map(repair => {
            const statusCfg = REPAIR_STATUS_CONFIG[repair.status as OptiBayRepairStatus] || REPAIR_STATUS_CONFIG.planned;
            return (
              <div key={repair.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs font-semibold">{repair.repair_description}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive"
                    onClick={() => removeRepair.mutate({ id: repair.id, sessionId: session.id })}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 pl-5 flex-wrap">
                  <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full font-medium', statusCfg.color)}>
                    {statusCfg.label}
                  </span>
                  {repair.repair_category && (
                    <Badge variant="outline" className="text-[10px]">{repair.repair_category}</Badge>
                  )}
                  {repair.estimated_minutes && (
                    <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                      <Clock className="h-2.5 w-2.5" />
                      {repair.estimated_minutes}m
                    </span>
                  )}
                </div>
                {repair.parts_needed && (
                  <p className="text-xs text-muted-foreground pl-5">Parts: {repair.parts_needed}</p>
                )}
                {repair.status === 'planned' && (
                  <div className="flex gap-1.5 pl-5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px]"
                      onClick={() => handleStatusChange(repair.id, 'in_progress')}
                    >
                      Start
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px]"
                      onClick={() => handleStatusChange(repair.id, 'cancelled')}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                {repair.status === 'in_progress' && (
                  <div className="flex gap-1.5 pl-5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-[10px] text-green-600"
                      onClick={() => handleStatusChange(repair.id, 'completed')}
                    >
                      Mark Complete
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm ? (
        <div className="border rounded-lg p-3 space-y-2">
          <Textarea
            placeholder="Describe the repair action…"
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="text-xs min-h-[60px]"
          />
          {interpretations.length > 0 && (
            <div>
              <label className="text-[10px] text-muted-foreground mb-1 block">Link to interpretation (optional)</label>
              <div className="flex gap-1 flex-wrap">
                {interpretations.map(interp => (
                  <button
                    key={interp.id}
                    type="button"
                    onClick={() => setSelectedInterpretationId(selectedInterpretationId === interp.id ? '' : interp.id)}
                    className={cn(
                      'text-[10px] px-2 py-0.5 rounded-full border transition-colors truncate max-w-[200px]',
                      selectedInterpretationId === interp.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'hover:bg-accent/50 text-muted-foreground',
                    )}
                  >
                    {interp.interpretation_text.slice(0, 40)}…
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <Input
              placeholder="Category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="text-xs h-8"
            />
            <Input
              placeholder="Parts needed"
              value={partsNeeded}
              onChange={e => setPartsNeeded(e.target.value)}
              className="text-xs h-8"
            />
            <Input
              placeholder="Est. minutes"
              type="number"
              value={estimatedMinutes}
              onChange={e => setEstimatedMinutes(e.target.value)}
              className="text-xs h-8"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAdd} disabled={!description.trim() || addRepair.isPending}>
              Add Repair
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Repair
        </Button>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button size="sm" onClick={onNext} disabled={repairs.length === 0}>
          Continue to Verify
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
